import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import Auxiliary from 'hoc/Auxiliary';
import VisibilityConnectionsGrid from './VisibilityConnection/VisibilityConnectionsGrid';
import ConnectionsProvidersList from './VisibilityConnection/ConnectionsProvidersList';
import { withStyles } from '@material-ui/core';
import SnackbarTrustCenter from './UI/Snackbar/TSnackbar';
import CustomHeader from '../CustomHeader/CustomHeader';
import { connect } from 'react-redux';
import Modal from '@material-ui/core/Modal';
import AgreementTerms from 'components/TermsConditions/AgreementTerms';
import { setLoggedInUser } from 'store/actions/termsAndConditionsActions';
import { setCurrentPage, getTerms } from 'store/actions';
import {
    checkCallUpdateFlag,
    getCloudHubConnectorStatus,
    getProviderAccounts
} from './store/actions';
import {
    CARRIER_V2_CONNECTIONS_PATH,
    CONNECTIONS,
    CONNECTIONS_V2,
    ROUTES
} from 'constants/Constants';
import { isNotBlank } from 'components/Common/Utils/StringUtils';
import { VISIBILITY_PROVIDER, SYSTEM_ADMIN } from 'constants/Constants';
import {
    CARRIER_AGREEMENT_SURFACES,
    getCarrierAgreementSurfaces,
    hasCarrierSurface,
    isCarrierAgreementContext,
    isCarrierV2Route,
    isLegacyCarrierV2Route
} from './carrierAgreementSurfaces';

/** @typedef {import('./sharingAgreementV2.typedefs.js').V2SharingAgreement} V2SharingAgreement */

/** When Titan is disabled, /connections lists all V2 providers (those with agreements first); drilling in lists {@link V2SharingAgreement} rows for one provider. */

const styles = () => ({
    customRoot: {
        height: 830,
        backgroundColor: '#F3F3F7',
        padding: '20px 50px'
    },
    availabilityError: {
        margin: '24px 0',
        padding: '16px',
        backgroundColor: '#FFF4F4',
        border: '1px solid #D0021B',
        borderRadius: 4,
        color: '#3C3C3C'
    },
    availabilityErrorTitle: {
        margin: '0 0 8px',
        fontWeight: 600
    }
});

const TrustCenter = (props) => {
    const {
        classes,
        setLoggedInUser,
        getTerms,
        loggedInUserTerms,
        visibilityProviderFlag,
        session,
        checkCallUpdateFlag,
        setCurrentPage,
        parentPage,
        isTitanServiceEnabled,
        isCloudHubConnectorActive,
        isCloudHubConnectorStatusLoaded,
        isCloudHubConnectorStatusError,
        getProviderAccounts,
        getCloudHubConnectorStatus,
        selectedAccount,
        match,
        location
    } = props;

    const filterProviderAccountId = match?.params?.providerAccountId;
    const filterProviderDisplayName = location?.state?.providerName;
    const carrierContext = isCarrierAgreementContext(session, selectedAccount);
    const isV2ConnectionsRoute = isCarrierV2Route(location?.pathname);
    const carrierAgreementSurfaces = getCarrierAgreementSurfaces({
        isCarrierContext: carrierContext,
        isTitanServiceEnabled,
        isCloudHubConnectorActive
    });
    const carrierAvailabilityError =
        carrierContext && isTitanServiceEnabled && isCloudHubConnectorStatusError;
    const carrierAvailabilityReady =
        !carrierAvailabilityError &&
        (!carrierContext || !isTitanServiceEnabled || isCloudHubConnectorStatusLoaded);
    const hasV1Surface = hasCarrierSurface(carrierAgreementSurfaces, CARRIER_AGREEMENT_SURFACES.V1);
    const hasV2Surface = hasCarrierSurface(carrierAgreementSurfaces, CARRIER_AGREEMENT_SURFACES.V2);
    const isVisibilityProvider =
        !session.isCarrier && session?.accountType?.includes(VISIBILITY_PROVIDER);
    const shouldShowConnectionsProvidersList =
        (!isTitanServiceEnabled &&
            isVisibilityProvider &&
            location.pathname === ROUTES.CONNECTIONS &&
            !filterProviderAccountId) ||
        (hasV2Surface && isV2ConnectionsRoute && !filterProviderAccountId);

    const [sessionValue, setSessionValues] = useState({
        isCarrier: session.isCarrier,
        userId: session.userId,
        isSystemAdmin: session.userRole === SYSTEM_ADMIN,
        isVisibilityProvider:
            session?.accountType?.includes(VISIBILITY_PROVIDER) &&
            session.userRole === VISIBILITY_PROVIDER,
        accountId: session.selected_accountId ? session.selected_accountId : session.accountId,
        userUUID: session?.userUUID
    });

    useEffect(() => {
        if (isNotBlank(parentPage)) {
            setCurrentPage(parentPage);
        } else if (isV2ConnectionsRoute) {
            setCurrentPage(CONNECTIONS_V2);
        } else {
            setCurrentPage(CONNECTIONS);
        }
    }, [setCurrentPage, parentPage, isV2ConnectionsRoute]);

    useEffect(() => {
        checkCallUpdateFlag(false);
    }, [checkCallUpdateFlag]);

    const [localLoggedInUserTerms, setLocalLoggedInUserTerms] = useState(loggedInUserTerms);

    useEffect(() => {
        setLocalLoggedInUserTerms(loggedInUserTerms);
    }, [loggedInUserTerms]);

    useEffect(() => {
        getTerms();
        if (!isTitanServiceEnabled || isV2ConnectionsRoute) {
            getProviderAccounts([VISIBILITY_PROVIDER]);
        }
    }, []);

    useEffect(() => {
        const accountId =
            session.userRole === SYSTEM_ADMIN && selectedAccount?.id
                ? selectedAccount.id
                : session.accountUuid;
        if (accountId) {
            getCloudHubConnectorStatus(accountId);
        }
    }, [session.userRole, session.accountUuid, selectedAccount?.id, getCloudHubConnectorStatus]);

    useEffect(() => {
        if (!carrierAvailabilityReady || !carrierContext) {
            return;
        }
        const search =
            session.userRole === SYSTEM_ADMIN && selectedAccount?.id
                ? `?accountId=${selectedAccount.id}`
                : location.search || '';
        if (isLegacyCarrierV2Route(location.pathname)) {
            if (hasV2Surface) {
                props.history.replace({
                    pathname: filterProviderAccountId
                        ? `${CARRIER_V2_CONNECTIONS_PATH}/provider/${filterProviderAccountId}`
                        : CARRIER_V2_CONNECTIONS_PATH,
                    search,
                    state: location.state
                });
            } else {
                props.history.replace({ pathname: ROUTES.CONNECTIONS, search });
            }
            return;
        }
        if (location.pathname === ROUTES.CONNECTIONS && !hasV1Surface && hasV2Surface) {
            props.history.replace({ pathname: ROUTES.CONNECTIONS_V2, search });
            return;
        }
        if (isV2ConnectionsRoute && !hasV2Surface && hasV1Surface) {
            props.history.replace({ pathname: ROUTES.CONNECTIONS, search });
        }
    }, [
        carrierAvailabilityReady,
        carrierContext,
        hasV1Surface,
        hasV2Surface,
        isV2ConnectionsRoute,
        location.pathname,
        location.search,
        location.state,
        filterProviderAccountId,
        session.userRole,
        selectedAccount?.id,
        props.history
    ]);

    useEffect(() => {
        setSessionValues({
            isCarrier: session.isCarrier,
            userId: session.userId,
            isSystemAdmin: session.userRole === 'SYSTEM_ADMIN',
            accountId: session.selected_accountId ? session.selected_accountId : session.accountId
        });
        if (!session.isCarrier && session?.accountType?.includes(VISIBILITY_PROVIDER)) {
            const visibilityProviderPath = isTitanServiceEnabled
                ? ROUTES.SHARING_AGREEMENTS
                : ROUTES.CONNECTIONS;
            if (location.pathname !== visibilityProviderPath) {
                props.history.push(visibilityProviderPath);
            }
        }
        if (!session.isCarrier && isNotBlank(session.selected_accountId)) {
            props.history.push(`/connections?accountId=${session.selected_accountId}`);
        }
    }, [session]);

    useEffect(() => {
        if (sessionValue.isCarrier && Object.entries(localLoggedInUserTerms).length === 0) {
            setLoggedInUser(sessionValue.accountId, sessionValue.userId, sessionValue?.userUUID);
        }
    }, [setLoggedInUser, localLoggedInUserTerms]);

    return (
        <Auxiliary>
            <Auxiliary>
                <Modal open={!localLoggedInUserTerms.is_agreed && props.loggedInUserTCPermission}>
                    <AgreementTerms />
                </Modal>
                <CustomHeader title="VISIBILITY CONNECTIONS" />
            </Auxiliary>
            <div className={classes.root}>
                {carrierAvailabilityError && (
                    <div className={classes.availabilityError} role="alert">
                        <p className={classes.availabilityErrorTitle}>
                            Trust Center availability unavailable
                        </p>
                        <p>
                            We could not determine agreement availability. Please reload the page or
                            try again later.
                        </p>
                    </div>
                )}
                {!carrierAvailabilityError &&
                    carrierAvailabilityReady &&
                    (shouldShowConnectionsProvidersList ? (
                        <ConnectionsProvidersList />
                    ) : (
                        <VisibilityConnectionsGrid
                            visibilityProviderFlag={visibilityProviderFlag}
                            sessionValue={session}
                            isTitanServiceEnabled={isTitanServiceEnabled}
                            filterProviderAccountId={filterProviderAccountId}
                            filterProviderDisplayName={filterProviderDisplayName}
                        />
                    ))}
                <SnackbarTrustCenter />
            </div>
        </Auxiliary>
    );
};

const mapStateToProps = (state) => ({
    loggedInUserTCPermission: state.termsAndConditions.loggedInUserTCPermission,
    loggedInUserTerms: state.termsAndConditions.loggedInUserTerms,
    session: state.session.sessionValue,
    selectedAccount: state.accounts.selectedAccount,
    isTitanServiceEnabled: state.agreementV2.isTitanServiceEnabled,
    isCloudHubConnectorActive: state.agreementV2.isCloudHubConnectorActive,
    isCloudHubConnectorStatusLoaded: state.agreementV2.isCloudHubConnectorStatusLoaded,
    isCloudHubConnectorStatusError: state.agreementV2.isCloudHubConnectorStatusError
});

export default connect(mapStateToProps, {
    setLoggedInUser,
    checkCallUpdateFlag,
    getTerms,
    setCurrentPage,
    getProviderAccounts,
    getCloudHubConnectorStatus
})(withStyles(styles)(withRouter(TrustCenter)));
